import PropTypes from "prop-types"
import { useMemo, useState } from "react"
import { SearchOutlined } from "@mui/icons-material"
import {
  Chip,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import SkillsSection from "connected-components/skills/components/skills-section"
import type {
  SkillCatalogEntry,
  SkillsLibraryTab,
} from "connected-components/skills/types"
import {
  buildSkillSections,
  collectSkillFacets,
  filterSkillSections,
  getAvailableFacets,
  hasPreviewableSkills,
} from "connected-components/skills/utils/sections"

import "./style.scss"

interface SkillsLibraryProps {
  licensedSkills?: SkillCatalogEntry[]
  proprietarySkills?: SkillCatalogEntry[]
  categoryOrder?: string[]
}

function toggleInList(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter(item => item !== value) : [...list, value]
}

function SkillsLibrary({
  licensedSkills = [],
  proprietarySkills = [],
  categoryOrder = [],
}: SkillsLibraryProps) {
  const [tab, setTab] = useState<SkillsLibraryTab>("all")
  const [query, setQuery] = useState("")
  const [roles, setRoles] = useState<string[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [showPreview, setShowPreview] = useState(false)

  const sections = useMemo(
    () => buildSkillSections(licensedSkills, proprietarySkills),
    [licensedSkills, proprietarySkills],
  )

  const allFacets = useMemo(() => collectSkillFacets(sections, categoryOrder), [sections, categoryOrder])

  // Drop selections that no longer exist in the catalog (e.g. after an account
  // switch) so a stale chip can't blank the list or linger as a ghost.
  const activeRoles = useMemo(() => roles.filter(role => allFacets.roles.includes(role)), [roles, allFacets])
  const activeCategories = useMemo(
    () => categories.filter(category => allFacets.categories.includes(category)),
    [categories, allFacets],
  )

  // Facet values still in scope under the tab/search/preview gates. Chip selections
  // are intentionally excluded — they OR together, so one never narrows another.
  const availableFacets = useMemo(
    () => getAvailableFacets(sections, { tab, query, showPreview }, categoryOrder),
    [sections, tab, query, showPreview, categoryOrder],
  )

  // A chip shows if it's available now OR currently selected — so a selection is
  // always visible and undoable; deselecting an unavailable chip then hides it.
  const visibleRoles = useMemo(
    () => allFacets.roles.filter(role => availableFacets.roles.includes(role) || activeRoles.includes(role)),
    [allFacets, availableFacets, activeRoles],
  )
  const visibleCategories = useMemo(
    () =>
      allFacets.categories.filter(
        category => availableFacets.categories.includes(category) || activeCategories.includes(category),
      ),
    [allFacets, availableFacets, activeCategories],
  )

  // The preview switch is only worth showing when there's a beta workflow in the
  // current tab/search scope to reveal.
  const canTogglePreview = useMemo(() => hasPreviewableSkills(sections, { tab, query }), [sections, tab, query])

  const visibleSections = useMemo(
    () => filterSkillSections(sections, { tab, query, roles: activeRoles, categories: activeCategories, showPreview }),
    [sections, tab, query, activeRoles, activeCategories, showPreview],
  )

  if (!sections.length) {
    return null
  }

  return (
    <section className="skills-library">
      <header className="skills-library-header">
        <div>
          <Typography variant="h4" component="h1" className="skills-library-title">
            Agentic Workflows
          </Typography>
        </div>
      </header>

      <div className="skills-library-toolbar">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={tab}
          onChange={(_, value: SkillsLibraryTab | null) => {
            if (value) {
              setTab(value)
            }
          }}
          className="skills-library-tabs"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="core">Core</ToggleButton>
          <ToggleButton value="tailored">Tailored</ToggleButton>
        </ToggleButtonGroup>

        <div className="skills-library-toolbar-trailing">
          <TextField
            size="small"
            placeholder="Search workflows"
            value={query}
            onChange={event => setQuery(event.target.value)}
            inputProps={{ "aria-label": "Search workflows" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
            className="skills-library-search"
          />

          {canTogglePreview && (
            <FormControlLabel
              className="skills-library-preview-toggle"
              control={
                <Switch
                  size="small"
                  checked={showPreview}
                  onChange={event => setShowPreview(event.target.checked)}
                />
              }
              label="Show preview workflows"
            />
          )}
        </div>
      </div>

      <div className="skills-library-filters">
        {visibleRoles.map(roleTag => (
          <Chip
            key={`role-${roleTag}`}
            size="small"
            label={roleTag}
            color={activeRoles.includes(roleTag) ? "primary" : "default"}
            variant={activeRoles.includes(roleTag) ? "filled" : "outlined"}
            onClick={() => setRoles(current => toggleInList(current, roleTag))}
          />
        ))}
        {visibleCategories.map(categoryTag => (
          <Chip
            key={`category-${categoryTag}`}
            size="small"
            label={categoryTag}
            color={activeCategories.includes(categoryTag) ? "primary" : "default"}
            variant={activeCategories.includes(categoryTag) ? "filled" : "outlined"}
            onClick={() => setCategories(current => toggleInList(current, categoryTag))}
          />
        ))}
      </div>

      <div className="skills-library-content">
        {visibleSections.length ? (
          visibleSections.map(section => (
            <SkillsSection
              key={section.id}
              section={section}
            />
          ))
        ) : (
          <Typography variant="body1" color="textSecondary" className="skills-library-empty">
            No agentic workflows match your filters.
          </Typography>
        )}
      </div>
    </section>
  )
}

const SKILL_ENTRY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  plugin: PropTypes.string,
  author: PropTypes.string,
  downloadFilename: PropTypes.string,
  lastUpdated: PropTypes.string,
  beta: PropTypes.bool,
  roles: PropTypes.arrayOf(PropTypes.string),
  categories: PropTypes.arrayOf(PropTypes.string),
})

SkillsLibrary.propTypes = {
  licensedSkills: PropTypes.arrayOf(SKILL_ENTRY_SHAPE),
  proprietarySkills: PropTypes.arrayOf(SKILL_ENTRY_SHAPE),
  categoryOrder: PropTypes.arrayOf(PropTypes.string),
}

export default SkillsLibrary
